import { z } from 'zod';
import { PAID_TIERS, PAYMENT_TIERS } from './payment-tiers';

// ---------- primitive building blocks ----------

export const ScoreSchema = z.object({
  team1: z.number().int().min(0).max(99),
  team2: z.number().int().min(0).max(99),
});

// ---------- prediction input (cascading rules) ----------

const rawPredictionSchema = z.object({
  fullTime: ScoreSchema,
  extraTime: ScoreSchema.nullable(),
  penalties: ScoreSchema.nullable(),
});

/**
 * Full cascading rules for prediction payload:
 *   - extraTime may only be set when fullTime is a draw
 *   - penalties may only be set when extraTime is a draw
 *   - penalties must have a winner (no ties)
 */
export const PredictionInputSchema = rawPredictionSchema
  .refine(
    (val) => !val.extraTime || val.fullTime.team1 === val.fullTime.team2,
    {
      message: 'extraTime only allowed when fullTime is a draw',
      path: ['extraTime'],
    },
  )
  .refine(
    (val) =>
      !val.extraTime ||
      (val.extraTime.team1 >= val.fullTime.team1 &&
        val.extraTime.team2 >= val.fullTime.team2),
    {
      message: 'extraTime score cannot be lower than fullTime for either team',
      path: ['extraTime'],
    },
  )
  .refine((val) => !val.penalties || val.extraTime !== null, {
    message: 'penalties require extraTime',
    path: ['penalties'],
  })
  .refine(
    (val) =>
      !val.penalties ||
      !val.extraTime ||
      val.extraTime.team1 === val.extraTime.team2,
    {
      message: 'penalties only allowed when extraTime is a draw',
      path: ['penalties'],
    },
  )
  .refine((val) => !val.penalties || val.penalties.team1 !== val.penalties.team2, {
    message: 'penalties must have a winner (no ties)',
    path: ['penalties'],
  });

export type PredictionInput = z.infer<typeof PredictionInputSchema>;

// ---------- auth ----------

export const SignupInputSchema = z
  .object({
    email: z.string().toLowerCase().email().max(254),
    password: z.string().min(8).max(128),
    passwordConfirm: z.string().min(1).max(128),
    displayName: z.string().trim().min(1).max(64),
    timezone: z.string().min(1).max(64).optional(),
    // Optional: a group-invite token carried through from /accept_group_invite/:token.
    // When present, the server stores it on the user and auto-joins the group on
    // email verification.
    inviteToken: z.string().min(1).max(256).optional(),
  })
  .refine((val) => val.password === val.passwordConfirm, {
    message: 'Passwords do not match',
    path: ['passwordConfirm'],
  });
export type SignupInput = z.infer<typeof SignupInputSchema>;

export const VerifyEmailInputSchema = z.object({
  token: z.string().min(1).max(256),
});
export type VerifyEmailInput = z.infer<typeof VerifyEmailInputSchema>;

export const ResendVerificationInputSchema = z.object({
  email: z.string().toLowerCase().email().max(254),
});
export type ResendVerificationInput = z.infer<typeof ResendVerificationInputSchema>;

export const RequestPasswordResetInputSchema = z.object({
  email: z.string().toLowerCase().email().max(254),
});
export type RequestPasswordResetInput = z.infer<typeof RequestPasswordResetInputSchema>;

export const ResetPasswordInputSchema = z
  .object({
    token: z.string().min(1).max(256),
    password: z.string().min(8).max(128),
    passwordConfirm: z.string().min(1).max(128),
  })
  .refine((val) => val.password === val.passwordConfirm, {
    message: 'Passwords do not match',
    path: ['passwordConfirm'],
  });
export type ResetPasswordInput = z.infer<typeof ResetPasswordInputSchema>;

export const LoginInputSchema = z.object({
  email: z.string().toLowerCase().email().max(254),
  password: z.string().min(1).max(128),
});
export type LoginInput = z.infer<typeof LoginInputSchema>;

export const GoogleAuthInputSchema = z.object({
  idToken: z.string().min(1),
  timezone: z.string().min(1).max(64).optional(),
  // Same semantics as SignupInputSchema.inviteToken — the token is consumed
  // during sign-in since Google already vouches for the email address.
  inviteToken: z.string().min(1).max(256).optional(),
});
export type GoogleAuthInput = z.infer<typeof GoogleAuthInputSchema>;

// ---------- groups ----------

export const GroupInputSchema = z.object({
  name: z.string().trim().min(1).max(64),
});
export type GroupInput = z.infer<typeof GroupInputSchema>;

export const InviteEmailInputSchema = z.object({
  email: z.string().toLowerCase().trim().email().max(254),
});
export type InviteEmailInput = z.infer<typeof InviteEmailInputSchema>;

// ---------- payments ----------

// Body of POST /api/payments/checkout-session — only paid tiers are valid;
// requesting `free` would mean "downgrade for free", which we don't support.
export const CheckoutSessionInputSchema = z.object({
  tier: z.enum(PAID_TIERS),
});
export type CheckoutSessionInput = z.infer<typeof CheckoutSessionInputSchema>;

// ---------- user profile updates ----------

export const UpdateFavoriteTeamInputSchema = z.object({
  teamId: z.string().min(1),
});

export const UpdateRemindersInputSchema = z.object({
  enabled: z.boolean(),
  timezone: z.string().min(1).max(64),
});

export const UpdateProfileInputSchema = z.object({
  displayName: z.string().trim().min(1).max(64),
});

// Admin-only manual payment-tier override. Used from the Profile page to set
// any tier (including downgrade to free) without going through Stripe — the
// Stripe webhook is upgrade-only, so a future paid event could still bump
// this back up.
export const UpdatePaymentTierInputSchema = z.object({
  tier: z.enum(PAYMENT_TIERS),
});
export type UpdatePaymentTierInput = z.infer<typeof UpdatePaymentTierInputSchema>;

// ---------- admin ----------

export const AdminUpdateUserStatusSchema = z.object({
  status: z.enum(['active', 'disabled']),
  reason: z.string().max(500).optional(),
});

export const AdminMatchResultSchema = z
  .object({
    fullTime: ScoreSchema,
    extraTime: ScoreSchema.nullable().optional(),
    penalties: ScoreSchema.nullable().optional(),
  })
  .refine(
    (val) =>
      !val.extraTime ||
      (val.extraTime.team1 >= val.fullTime.team1 &&
        val.extraTime.team2 >= val.fullTime.team2),
    {
      message: 'extraTime score cannot be lower than fullTime for either team',
      path: ['extraTime'],
    },
  );

export const AdminToggleFlagSchema = z.object({
  enabled: z.boolean(),
});
